import React, { Fragment, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../constants';
import { formatSOL, formatSOLAmount } from '../utils/formatSol';

interface Subscription {
  id: string;
  type: 'monthly' | 'annual';
  amount: number;
  startDate: string;
  endDate: string;
  status: 'active' | 'cancelled' | 'expired';
  daysRemaining: number;
  isExpired: boolean;
  user: {
    id: string;
    username: string;
    email: string;
    avatar?: string;
    role: string;
    monthlyPrice?: number;
    yearlyPrice?: number;
  };
}

const MySubscriptions: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'cancelled' | 'expired'>('active');
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadSubscriptions();
  }, [filter]);

  const loadSubscriptions = async () => {
    setLoading(true);
    setError('');
    try {
      const statusParam = filter === 'all' ? '' : `?status=${filter}`;
      const response = await fetch(`${API_URL}/api/subscriptions/my-subscriptions${statusParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
      } else {
        setError('Failed to load subscriptions');
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) {
      return;
    }

    console.log('Cancelling subscription:', {
      subscriptionId,
      apiUrl: `${API_URL}/api/subscriptions/${subscriptionId}/cancel`,
      hasToken: !!token
    });

    try {
      const response = await fetch(`${API_URL}/api/subscriptions/${subscriptionId}/cancel`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Cancel response status:', response.status);

      if (response.ok) {
        const responseData = await response.json();
        console.log('Cancel response data:', responseData);

        setError('');
        loadSubscriptions(); // Refresh the list
        alert('Subscription cancelled successfully!');
      } else {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        console.error('Cancel error response:', errorData);
        setError(errorData.error || `Failed to cancel subscription (${response.status})`);
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setError(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleViewProfile = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  const getStatusBadge = (status: string, isExpired: boolean) => {
    if (isExpired) {
      return <span className="badge bg-danger font-xssss">Expired</span>;
    }
    switch (status) {
      case 'active':
        return <span className="badge bg-success font-xssss">Active</span>;
      case 'cancelled':
        return <span className="badge bg-warning font-xssss">Cancelled</span>;
      case 'expired':
        return <span className="badge bg-danger font-xssss">Expired</span>;
      default:
        return <span className="badge bg-secondary font-xssss">Unknown</span>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    if (filter === 'all') return true;
    if (filter === 'expired') return sub.isExpired;
    if (filter === 'active') return sub.status === 'active' && !sub.isExpired;
    return sub.status === filter;
  });

  const getFilterCount = (filterType: string) => {
    switch (filterType) {
      case 'active':
        return subscriptions.filter(s => s.status === 'active' && !s.isExpired).length;
      case 'expired':
        return subscriptions.filter(s => s.isExpired).length;
      case 'cancelled':
        return subscriptions.filter(s => s.status === 'cancelled').length;
      case 'all':
        return subscriptions.length;
      default:
        return 0;
    }
  };

  return (
    <Fragment>
      <div className="container-fluid py-2 py-md-4">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10 col-xl-8">
            {/* Header */}
            <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
              <div className="card-body p-0">
                <h2 className="fw-700 font-lg text-grey-900 mb-2">My Subscriptions</h2>
                <p className="font-xsss text-grey-500 mb-0">Manage your creator subscriptions</p>
              </div>
            </div>

            {error && (
              <div className="alert alert-danger font-xss fw-500 mb-3">
                {error}
              </div>
            )}

            {/* Filter Tabs */}
            <div className="card shadow-xss w-100 d-block border-0 p-0 mb-3">
              <div className="card-body p-0">
                <ul className="nav nav-tabs h55 d-flex product-info-tab border-bottom-0 ps-4" role="tablist">
                  {[
                    { key: 'active', label: 'Active' },
                    { key: 'expired', label: 'Expired' },
                    { key: 'cancelled', label: 'Cancelled' },
                    { key: 'all', label: 'All' }
                  ].map(({ key, label }) => (
                    <li key={key} className="list-inline-item me-5">
                      <a
                        className={`fw-700 font-xsss text-grey-500 pt-3 pb-3 ls-1 d-inline-block ${filter === key ? 'active' : ''}`}
                        onClick={() => setFilter(key as any)}
                        style={{ cursor: 'pointer' }}
                      >
                        {label} ({getFilterCount(key)})
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Content */}
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="font-xsss text-grey-500 mt-2">Loading subscriptions...</p>
              </div>
            ) : filteredSubscriptions.length > 0 ? (
              <div className="row">
                {filteredSubscriptions.map((subscription) => (
                  <div key={subscription.id} className="col-12 col-md-6 col-lg-4 mb-3">
                    <div className="card shadow-xss w-100 d-block border-0 p-4">
                      <div className="card-body p-0">
                        {/* User Info */}
                        <div className="d-flex align-items-center mb-3">
                          <img
                            src={subscription.user.avatar || '/assets/images/user.png'}
                            alt="Profile"
                            className="rounded-circle me-3"
                            style={{ width: '50px', height: '50px', objectFit: 'cover', cursor: 'pointer' }}
                            onClick={() => handleViewProfile(subscription.user.id)}
                          />
                          <div className="flex-grow-1">
                            <h5
                              className="fw-600 font-xss text-grey-900 mb-1"
                              onClick={() => handleViewProfile(subscription.user.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              {subscription.user.username || subscription.user.email?.split('@')[0]}
                            </h5>
                            <p className="font-xssss text-grey-500 mb-0">{subscription.user.email}</p>
                          </div>
                          {getStatusBadge(subscription.status, subscription.isExpired)}
                        </div>

                        {/* Subscription Details */}
                        <div className="mb-3">
                          <div className="d-flex align-items-center mb-2">
                            <i className="feather-dollar-sign font-xss text-grey-700 me-2"></i>
                            <span className="font-xsss text-grey-900 fw-600">{formatSOL(subscription.amount)}</span>
                            <span className="font-xsss text-grey-500 mx-1">/</span>
                            <span className="font-xsss text-grey-500">{subscription.type}</span>
                          </div>

                          <div className="d-flex align-items-center mb-2">
                            <i className="feather-calendar font-xss text-grey-700 me-2"></i>
                            <span className="font-xsss text-grey-500">
                              {formatDate(subscription.startDate)} - {formatDate(subscription.endDate)}
                            </span>
                          </div>

                          {subscription.status === 'active' && !subscription.isExpired && (
                            <div className="d-flex align-items-center">
                              <i className="feather-clock font-xss text-grey-700 me-2"></i>
                              <span className={`font-xsss ${subscription.daysRemaining <= 7 ? 'text-danger' : 'text-grey-500'}`}>
                                {subscription.daysRemaining} days remaining
                              </span>
                              {subscription.daysRemaining <= 7 && (
                                <i className="feather-alert-circle font-xss text-danger ms-1"></i>
                              )}
                            </div>
                          )}

                          {subscription.isExpired && (
                            <div className="d-flex align-items-center">
                              <i className="feather-alert-circle font-xss text-danger me-2"></i>
                              <span className="font-xsss text-danger">Subscription expired</span>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="d-flex gap-2">
                          <button
                            onClick={() => handleViewProfile(subscription.user.id)}
                            className="btn bg-greylight text-grey-900 font-xsss fw-600 p-2 rounded-3 flex-grow-1"
                          >
                            <i className="feather-user me-1"></i>
                            View Profile
                          </button>

                          {subscription.status === 'active' && !subscription.isExpired && (
                            <button
                              onClick={() => handleCancelSubscription(subscription.id)}
                              className="btn bg-danger text-white font-xsss fw-600 p-2 rounded-3"
                            >
                              <i className="feather-user-minus me-1"></i>
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card shadow-xss w-100 d-block border-0 p-4">
                <div className="card-body text-center p-5">
                  <i className="feather-users font-xl text-grey-400 mb-3"></i>
                  <h5 className="fw-600 font-xss text-grey-900 mb-2">No subscriptions found</h5>
                  <p className="font-xsss text-grey-500 mb-3">
                    {filter === 'all'
                      ? "You haven't subscribed to any creators yet."
                      : `No ${filter} subscriptions found.`
                    }
                  </p>
                  <button
                    onClick={() => navigate('/profile-search')}
                    className="btn bg-current text-white font-xsss fw-600 p-3 rounded-3"
                  >
                    <i className="feather-search me-2"></i>
                    Discover Creators
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default MySubscriptions;
